import { action, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { CatalogState } from '../../components/layout/catalogState';
import { Error, loadError, pageError } from '../../core/error';
import { UserDashboardView } from '../../routes';
import { JobCatalogSource, JobModel } from '../../entities';
import { LibraryLabels } from '../../services/libraries/libraryServiceBase';
import { IntegrationTabState } from '../integrations/integrationTabState';
import { ZoomIntegrationTabState } from '../integrations/zoomIntegrationTabState';
import { SearchBarState } from '../../components/search/searchBarState';

type Props = {}
export type UserDashboardPageParams = {}

export type NavigationTabs = LibraryLabels | 'My Uploads';
export class UserDashboardPageState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'getTabFromQuery' | 'setError' | 'setLoaded'>(this, {
      isLoading: observable,
      error: observable,
      activeTab: observable,
      mounted: action,
      unmounted: action,
      getTabFromQuery: action,
      navigateToTab: action,
      setPagesize: action,
      reset: action,
      setError: action,
      setLoaded: action,
    });

    this.searchBarState = new SearchBarState(this.store, { searchMode: 'idle' });
    this.jobCatalogSource = new JobCatalogSource(this.store, {
      pageSize: 20,
      allJobs: true,
      jobFilter: (job: JobModel) => !job.source.externalLibrary
    });
    this.jobCatalog = new CatalogState(this.store, {
      syncStatus: () => this.jobCatalogSource.syncStatus,
      isEndOfList: () => this.jobCatalogSource.isEndOfList
    });
    this.jobCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.jobCatalogSource.fetchMore();
          break;
      }
    });

    // #region Google Drive tab state
    this.googleDriveTabState = new IntegrationTabState(this.store, {
      pageSize: 20,
      libraryService: this.store.googleDrive
    });
    // #endregion

    // #region One Drive source state
    this.oneDriveTabState = new IntegrationTabState(this.store, {
      pageSize: 20,
      libraryService: this.store.oneDrive,
      defaultSearchValue: this.store.oneDrive.defaultSearchValue
    });
    // #endregion

    // #region Zoom source state
    this.zoomIntegrationTabState = new ZoomIntegrationTabState(this.store, {
      pageSize: 20,
      libraryService: this.store.zoom
    });
    // #endregion
  }

  isLoading: boolean = false;
  error: Error | null = null;
  activeTab: NavigationTabs = 'My Uploads';

  readonly jobCatalogSource: JobCatalogSource;
  readonly jobCatalog = new CatalogState(this.store);

  readonly googleDriveTabState: IntegrationTabState;

  readonly oneDriveTabState: IntegrationTabState;

  readonly zoomIntegrationTabState: ZoomIntegrationTabState;
  readonly searchBarState: SearchBarState;

  async mounted(_params: UserDashboardPageParams) {

    this.reset();
    const search = new URLSearchParams(window.location.search);
    const view: string | null = search.get('view');
    const { user } = this.store;
    if (view && user?.hasPermission('UserUpload')) {
      this.activeTab = (this.getTabFromQuery(view as UserDashboardView));
    }
    this.isLoading = true;

    const [, err] = await this.jobCatalogSource.fetch();
    // The raw error text ("Failed to fetch", a server message) means nothing to the user.
    if (err)
      return this.setError(loadError('your uploads'));

    // Fetch teams for permissions
    await this.store.teamManager.apiEnsureTeams();
    this.setLoaded();
  }

  unmounted() {
    this.reset();
  }

  private getTabFromQuery(view: UserDashboardView): NavigationTabs {
    if (view === 'zoom' && this.zoomIntegrationTabState.enabled) return 'Zoom';
    if (view === 'onedrive' && this.oneDriveTabState.enabled) return 'OneDrive';
    if (view === 'gdrive' && this.googleDriveTabState.enabled) return 'Google Drive';

    if (view) this.navigateToTab('My Uploads');
    return 'My Uploads';
  }

  navigateToTab(tab: NavigationTabs) {
    const { uploadService } = this.store;

    switch (tab) {
      case 'Google Drive':
        return uploadService.navigateToGoogleDriveTab();
      case 'Zoom':
        return uploadService.navigateToZoomTab();
      case 'OneDrive':
        return uploadService.navigateToOneDriveTab();
      default:
        return uploadService.clearLibraryTabNavigation();
    }
  }

  setPagesize(pageSize: number) {
    this.googleDriveTabState.setPagesize(pageSize)
    this.zoomIntegrationTabState.setPagesize(pageSize);
    this.oneDriveTabState.setPagesize(pageSize);
    this.jobCatalogSource.setPagesizeValue(pageSize);
  }

  // #region State helpers
  reset() {
    this.isLoading = false;
    this.error = null;
    this.activeTab = 'My Uploads';
    this.searchBarState.reset();
    this.jobCatalogSource.reset();
    this.store.searchService.resetSearchResults();
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion
}